import { createQueryKeys } from '@lukemorales/query-key-factory';

import { get } from '@/api/api';
import { buildQueryParams } from '@/hooks/api/helpers';
import type {
  FanguardApiContractsDevicesDeviceModel,
  FanguardApiContractsSensorSensorGroupModel,
  FanguardApiContractsSensorSensorModel,
} from 'src/api/generated';

export const SENSORS_URI = 'sensors';

export interface SensorLogsResponse {
  logs: string;
}

export interface SensorMetrics {
  series: {
    0: {
      data: { x: string; y: number }[];
      key: string;
      name: string;
    };
  };
}

export const sensorsQueries = createQueryKeys(SENSORS_URI, {
  all: null,
  list: {
    queryKey: ['list'] as const,
    queryFn: async () => {
      const response = await get<FanguardApiContractsSensorSensorModel[]>({ uri: SENSORS_URI });
      return response.data;
    },
  },
  sensorGroups: {
    queryKey: ['sensorgroups'] as const,
    queryFn: async () => {
      const response = await get<FanguardApiContractsSensorSensorGroupModel[]>({ uri: `${SENSORS_URI}/groups` });
      return response.data.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0);
    },
  },
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsDevicesDeviceModel>({ uri: `${SENSORS_URI}/${id}` });
      return response.data;
    },
  }),
  metrics: (sensorId: string, type: 'cpu' | 'memory' | 'fps') => ({
    queryKey: [sensorId, 'metrics', type],
    queryFn: async () => {
      const params = buildQueryParams({ metric: type });
      const response = await get<SensorMetrics>({ uri: `${SENSORS_URI}/${sensorId}/graph?${params}` });
      return response.data;
    },
  }),
  logs: (id: string, lineCount?: number) => ({
    queryKey: [id, lineCount ?? 'all'] as const,
    queryFn: async () => {
      const suffix = lineCount ? `?lineCount=${lineCount}` : '';
      const response = await get<SensorLogsResponse>({ uri: `${SENSORS_URI}/${id}/log${suffix}` });
      return response.data;
    },
  }),
  group: (id: string) => ({
    queryKey: ['groups', id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsSensorSensorGroupModel>({ uri: `${SENSORS_URI}/groups/${id}` });
      return response.data;
    },
  }),
});
